import { z } from 'zod'
import { err, ok } from '../utils/result.ts'
import type { AIModel } from '../types/enums.ts'
import type { Env } from '../types/env.ts'
import { BRAND_VISIBILITY_TOOL, TOOL_NAME } from './tool-schema.ts'
import { buildBatchPrompt } from './prompt.ts'
import type { AIIntegration, BatchMentionResult, BatchResponse } from './types.ts'
import { SENTIMENTS } from '../types/enums.ts'

const MODEL_IDS: Record<'gpt-4o-mini' | 'gpt-4.1', string> = {
  'gpt-4o-mini': 'gpt-4o-mini',
  'gpt-4.1':     'gpt-4.1',
}

const ToolCallResultSchema = z.object({
  results: z.array(z.object({
    prompt_id:           z.string(),
    mentioned:           z.boolean(),
    rank_position:       z.number().int().nullable().optional(),
    sentiment:           z.enum(SENTIMENTS).nullable().optional(),
    context_snippet:     z.string().nullable().optional(),
    competitor_mentions: z.array(z.string()).optional(),
    ai_answer:           z.string().optional(),
  })),
})

const OpenAIResponseSchema = z.object({
  choices: z.array(z.object({
    message: z.object({
      tool_calls: z.array(z.object({
        function: z.object({
          name: z.string(),
          arguments: z.string(),
        }),
      })).min(1),
    }),
  })).min(1),
  usage: z.object({
    prompt_tokens: z.number().nullable().optional(),
    completion_tokens: z.number().nullable().optional(),
  }).optional(),
})

type OpenAIVariant = 'gpt-4o-mini' | 'gpt-4.1'

export function createOpenAIIntegration(variant: OpenAIVariant): AIIntegration {
  return {
    model: variant as AIModel,

    async queryBatch(params, env: Env) {
      const prompt = buildBatchPrompt(params)
      let res: Response
      try {
        res = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${env.OPENAI_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: MODEL_IDS[variant],
            messages: [{ role: 'user', content: prompt }],
            tools: [{ type: 'function', function: BRAND_VISIBILITY_TOOL }],
            tool_choice: { type: 'function', function: { name: TOOL_NAME } },
            max_tokens: 4096,
            temperature: 0.3,
          }),
        })
      } catch (e) {
        return err(`OpenAI network error: ${e instanceof Error ? e.message : String(e)}`)
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        return err(`OpenAI API error ${res.status}: ${text}`)
      }

      let data: unknown
      try { data = await res.json() } catch {
        return err('OpenAI returned invalid JSON')
      }

      const parsed = OpenAIResponseSchema.safeParse(data)
      if (!parsed.success) return err(`OpenAI response parse error: ${parsed.error.message}`)

      const argsStr = parsed.data.choices[0]?.message.tool_calls[0]?.function.arguments
      if (!argsStr) return err('OpenAI returned no tool call arguments')

      let toolInput: unknown
      try { toolInput = JSON.parse(argsStr) } catch {
        return err('OpenAI tool call arguments are not valid JSON')
      }

      const toolParsed = ToolCallResultSchema.safeParse(toolInput)
      if (!toolParsed.success) return err(`OpenAI tool result parse error: ${toolParsed.error.message}`)

      const promptMap = new Map(params.prompts.map((p) => [p.id, p.promptText]))

      const results: BatchMentionResult[] = toolParsed.data.results.map((r) => ({
        keywordId: r.prompt_id,
        promptText: promptMap.get(r.prompt_id) ?? '',
        mentioned: r.mentioned,
        rankPosition: r.rank_position ?? null,
        sentiment: r.sentiment ?? null,
        contextSnippet: r.context_snippet ?? null,
        competitorMentions: r.competitor_mentions ?? [],
        aiAnswer: r.ai_answer ?? null,
      }))

      const response: BatchResponse = {
        results,
        usage: {
          inputTokens: parsed.data.usage?.prompt_tokens ?? null,
          outputTokens: parsed.data.usage?.completion_tokens ?? null,
        },
      }
      return ok(response)
    },
  }
}
